/**
 * Route templates consumed by react-router. Two prefix variants per page so
 * services can be opened with or without an explicit owning building in the
 * path:
 *
 *   /providers/:providerUuid/events/:serviceUuid
 *   /building/:buildingUuid/providers/:providerUuid/events/:serviceUuid
 *
 * `useOpenParams()` exposes the path-side `:buildingUuid` as
 * `serviceBuildingUuid` so it never collides with the user's
 * `userBuildingUuid` (from the query string).
 */
export const ROUTES = {
  EVENT_DETAIL: [
    '/providers/:providerUuid/events/:serviceUuid',
    '/building/:buildingUuid/providers/:providerUuid/events/:serviceUuid',
  ],
  EVENT_TIME_SLOTS: [
    '/providers/:providerUuid/events/:serviceUuid/slots',
    '/building/:buildingUuid/providers/:providerUuid/events/:serviceUuid/slots',
  ],
  EVENT_TIME_SLOT_DETAILS: [
    '/providers/:providerUuid/events/:serviceUuid/slots/:timeSlotUuid',
    '/building/:buildingUuid/providers/:providerUuid/events/:serviceUuid/slots/:timeSlotUuid',
  ],
  // Inbound notification deeplink (SMB-999 mints `/deeplink/{eventUuid}`). The
  // uuid rides the path as `:serviceUuid` so `useOpenParams()` — keyed to the
  // service param names — surfaces it; the DeeplinkPage resolves it to the
  // two-segment detail path.
  DEEPLINK: '/deeplink/:serviceUuid',
  ERROR: '/error',
} as const;

export interface ServicePathParams {
  providerUuid: string;
  serviceUuid: string;
  /** Service's owning building — emits the `/building/:buildingUuid/...` prefix when set. */
  buildingUuid?: string;
}

const servicePrefix = ({ providerUuid, buildingUuid, serviceUuid }: ServicePathParams): string => {
  const buildingPart = buildingUuid ? `/building/${buildingUuid}` : '';
  return `${buildingPart}/providers/${providerUuid}/events/${serviceUuid}`;
};

export const buildEventDetailPath = (params: ServicePathParams): string => servicePrefix(params);

export const buildEventTimeSlotsPath = (params: ServicePathParams): string =>
  `${servicePrefix(params)}/slots`;

export const buildEventTimeSlotDetailsPath = (
  params: ServicePathParams & { timeSlotUuid: string },
): string => `${servicePrefix(params)}/slots/${params.timeSlotUuid}`;
